import React, { useState, useEffect, useMemo } from 'react'
import { ForecastData } from '../../types/ai'
import EnergyForecastChart from '../../components/charts/EnergyForecastChart'
import WeeklyBreakdownChart from '../../components/charts/WeeklyBreakdownChart'

const EnergyForecast: React.FC = () => {
  const [forecastData, setForecastData] = useState<ForecastData[]>([])
  const [forecastRange, setForecastRange] = useState('7d')
  const [viewType, setViewType] = useState<'hourly' | 'daily' | 'weekly'>('daily')

  const generateForecastData = useMemo(() => {
    const days = forecastRange === '7d' ? 7 : forecastRange === '30d' ? 30 : 90
    return Array.from({ length: days }, (_, i) => {
      const base = 800 + (i % 7) * 50
      return {
        date: new Date(Date.now() + i * 24 * 3600000).toISOString().split('T')[0],
        predicted_kwh: base + (i * 10),
        confidence_interval: [base - 100, base + 150] as [number, number],
        weather_factor: 0.85
      }
    })
  }, [forecastRange])

  useEffect(() => {
    setForecastData(generateForecastData)
  }, [generateForecastData])



  const weeklyData = useMemo(() => [
    { week: 'Week 1', predicted: 4200, actual: 3950 },
    { week: 'Week 2', predicted: 4800, actual: 4650 },
    { week: 'Week 3', predicted: 3900, actual: 3750 },
    { week: 'Week 4', predicted: 5100, actual: 4900 }
  ], []);

  const totalForecast = forecastData.reduce((sum, d) => sum + d.predicted_kwh, 0)
  const avgConfidence = forecastData.reduce((sum, d) => sum + d.weather_factor, 0) / forecastData.length * 100

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Energy Forecast</h1>
        <div className="flex gap-4">
          <select 
            value={viewType} 
            onChange={(e) => setViewType(e.target.value as 'hourly' | 'daily' | 'weekly')}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
          <select 
            value={forecastRange} 
            onChange={(e) => setForecastRange(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="7d">7 Days</option>
            <option value="30d">30 Days</option>
            <option value="90d">90 Days</option>
          </select>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Total Forecast</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
            {totalForecast.toFixed(0)} kWh
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Avg Confidence</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
            {avgConfidence.toFixed(1)}%
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Peak Day</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
            {Math.max(...forecastData.map(d => d.predicted_kwh)).toFixed(0)} kWh
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Model</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
            Prophet-v2.1
          </p>
        </div>
      </div>

      {/* Forecast Chart */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Energy Generation Forecast
        </h3>
        <EnergyForecastChart data={forecastData} />
      </div>

      {/* Weekly Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Weekly Breakdown
          </h3>
          <WeeklyBreakdownChart data={weeklyData} />
        </div>

        {/* Seasonal Heatmap */}
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Seasonal Forecast Pattern
          </h3>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 28 }, (_, i) => {
              const intensity = 0.3 + ((i % 7) * 0.1)
              return (
                <div 
                  key={i}
                  className="aspect-square rounded-lg flex items-center justify-center text-xs font-semibold text-white"
                  style={{ backgroundColor: `rgba(99, 102, 241, ${intensity})` }}
                >
                  {i + 1}
                </div>
              )
            })}
          </div>
          <div className="flex justify-between mt-4 text-sm font-medium text-slate-600">
            <span>Low Generation</span>
            <span>High Generation</span>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Export Forecast
        </h3>
        <div className="flex gap-4">
          <button className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl">
            Export CSV
          </button>
          <button className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl">
            Export PDF Report
          </button>
          <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl">
            Schedule Report
          </button>
        </div>
      </div>
    </div>
  )
}

export default EnergyForecast